export type ModuleLoadFailureCategory =
  | "IMPORTING_MODULE_SCRIPT_FAILED"
  | "DYNAMIC_IMPORT_FETCH_FAILED"
  | "CHUNK_LOAD_ERROR";

export interface ModuleLoadFailure {
  category: ModuleLoadFailureCategory;
  requestedUrl?: string;
  message: string;
}

interface SessionGuard {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ModuleRecoveryRuntime {
  sessionStorage: SessionGuard;
  reload(): void;
}

export type ModuleRecoveryResult =
  | "NOT_MODULE_LOAD_FAILURE"
  | "RELOAD_REQUESTED"
  | "RELOAD_ALREADY_ATTEMPTED"
  | "RECOVERY_UNAVAILABLE";

export const MODULE_RECOVERY_GUARD_KEY = "mbip.module-load-recovery-attempted.v1";

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null) {
    const candidate = error as { name?: unknown; message?: unknown };
    return [candidate.name, candidate.message]
      .filter((value): value is string => typeof value === "string")
      .join(": ");
  }
  return String(error);
}

function requestedModuleUrl(message: string): string | undefined {
  const match = message.match(
    /(?:https?:\/\/[^\s"')]+|\/assets\/[^\s"')]+)/i,
  );
  const raw = match?.[0].replace(/[.,;:]$/, "");
  if (raw === undefined) return undefined;
  try {
    const parsed = new URL(raw, "https://mbip.invalid");
    return raw.startsWith("/") ? parsed.pathname : `${parsed.origin}${parsed.pathname}`;
  } catch {
    return undefined;
  }
}

export function classifyModuleLoadFailure(
  error: unknown,
): ModuleLoadFailure | undefined {
  const message = errorText(error);
  const normalized = message.toLowerCase();

  let category: ModuleLoadFailureCategory | undefined;
  if (normalized.includes("importing a module script failed")) {
    category = "IMPORTING_MODULE_SCRIPT_FAILED";
  } else if (
    normalized.includes("failed to fetch dynamically imported module") ||
    normalized.includes("error loading dynamically imported module") ||
    normalized.includes("dynamic import module fetch")
  ) {
    category = "DYNAMIC_IMPORT_FETCH_FAILED";
  } else if (
    normalized.includes("chunkloaderror") ||
    /loading (?:css )?chunk [^ ]+ failed/.test(normalized)
  ) {
    category = "CHUNK_LOAD_ERROR";
  }

  if (category === undefined) return undefined;
  return { category, requestedUrl: requestedModuleUrl(message), message };
}

function browserRuntime(): ModuleRecoveryRuntime | undefined {
  if (typeof window === "undefined") return undefined;
  return {
    sessionStorage: window.sessionStorage,
    reload: () => window.location.reload(),
  };
}

export function attemptModuleLoadRecovery(
  error: unknown,
  runtime: ModuleRecoveryRuntime | undefined = browserRuntime(),
): ModuleRecoveryResult {
  const failure = classifyModuleLoadFailure(error);
  if (failure === undefined) return "NOT_MODULE_LOAD_FAILURE";
  if (runtime === undefined) return "RECOVERY_UNAVAILABLE";

  try {
    if (runtime.sessionStorage.getItem(MODULE_RECOVERY_GUARD_KEY) === "1") {
      return "RELOAD_ALREADY_ATTEMPTED";
    }
    runtime.sessionStorage.setItem(MODULE_RECOVERY_GUARD_KEY, "1");
    runtime.reload();
    return "RELOAD_REQUESTED";
  } catch {
    return "RECOVERY_UNAVAILABLE";
  }
}
